import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import AddButton from 'components/AddButton/AddButton';
import ConfirmDialog from 'components/ConfirmDialog';
import { removeElementByIndex, replaceElement } from 'helpers/arrays';
import {
  getJourneyPatternNames,
  JourneyPatternNameValidationError,
  validateJourneyPatternName as validateJourneyPatternNameUtil,
} from 'validation';
import useUniqueKeys from 'hooks/useUniqueKeys';
import JourneyPattern, { initJourneyPattern } from 'model/JourneyPattern';
import { ReactElement, useState } from 'react';
import { useIntl } from 'react-intl';
import StopPoint from '../../model/StopPoint';
import { createUuid } from '../../helpers/generators';
import NewJourneyPatternModal from './NewJourneyPatternModal';
import CopyDialog from '../JourneyPatternEditor/CopyDialog';

type Props = {
  journeyPatterns: JourneyPattern[];
  onChange: (journeyPatterns: JourneyPattern[]) => void;
  children: (
    journeyPattern: JourneyPattern,
    validateJourneyPatternName: (
      newJourneyPatternName: string | null,
    ) => JourneyPatternNameValidationError,
    handleUpdate: (journeyPattern: JourneyPattern) => void,
    handleCopy?: (jpName: string) => void,
    handleDelete?: () => void,
  ) => ReactElement;
};

// Re-export for backwards compatibility
export type { JourneyPatternNameValidationError } from 'validation';

const JourneyPatterns = ({ journeyPatterns, onChange, children }: Props) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [copyIndex, setCopyIndex] = useState<number | null>(null);
  const { formatMessage } = useIntl();

  const keys = useUniqueKeys(journeyPatterns);

  const updateJourneyPattern = (index: number) => {
    return (journeyPattern: JourneyPattern) => {
      onChange(replaceElement(journeyPatterns, index, journeyPattern));
    };
  };

  const deleteJourneyPattern = (index: number) => {
    return () => {
      if (journeyPatterns.length > 1) {
        onChange(removeElementByIndex(journeyPatterns, index));
      }
    };
  };

  const addNewJourneyPattern = (name: string) => {
    const newJourneyPatterns = [
      ...journeyPatterns,
      {
        ...initJourneyPattern(),
        name,
      },
    ];
    onChange(newJourneyPatterns);
    setShowModal(false);
    setTimeout(
      () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }),
      100,
    );
  };

  const copyJourneyPattern = (
    journeyPatterns: JourneyPattern[],
    journeyPatternIndex: number,
    name: string,
  ) => {
    const journeyPatternToCopy = journeyPatterns[journeyPatternIndex];
    const newJourneyPattern: JourneyPattern = {
      ...journeyPatternToCopy,
      id: undefined,
      name: name,
    };
    newJourneyPattern.pointsInSequence =
      journeyPatternToCopy.pointsInSequence.map((point: StopPoint) => ({
        ...point,
        id: undefined,
        key: createUuid(),
      }));
    newJourneyPattern.serviceJourneys = [
      {
        id: `new_${createUuid()}`,
        passingTimes: journeyPatternToCopy.pointsInSequence.map((_) => ({})),
      },
    ];
    const newJourneyPatterns: JourneyPattern[] = [
      ...journeyPatterns,
      newJourneyPattern,
    ];
    onChange(newJourneyPatterns);
  };

  const validateJourneyPatternName = (
    newJourneyPatternName: string | null,
  ): JourneyPatternNameValidationError => {
    return validateJourneyPatternNameUtil(
      newJourneyPatternName,
      getJourneyPatternNames(journeyPatterns),
      formatMessage({ id: 'nameIsRequired' }),
      formatMessage({ id: 'journeyPatternDuplicateNameValidationError' }),
    );
  };

  return (
    <>
      <NewJourneyPatternModal
        open={showModal}
        onSave={addNewJourneyPattern}
        onDismiss={() => setShowModal(false)}
        validateJourneyPatternName={validateJourneyPatternName}
      />

      <Box>
        <Typography variant="h1">
          {formatMessage({ id: 'editorJourneyPatternsTabLabel' })}
        </Typography>
        <Typography variant="body1">
          {formatMessage({ id: 'editorFillInformation' })}
        </Typography>
        {journeyPatterns.length === 1 ? (
          children(
            journeyPatterns[0],
            validateJourneyPatternName,
            updateJourneyPattern(0),
            (name: string) => copyJourneyPattern(journeyPatterns, 0, name),
            undefined,
          )
        ) : (
          <>
            {journeyPatterns.map((jp: JourneyPattern, index: number) => (
              <Accordion
                key={jp.id ?? keys[index]}
                defaultExpanded={!jp.id || index === journeyPatterns.length - 1}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    },
                  }}
                >
                  <Typography>{jp.name}</Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip
                      title={formatMessage({ id: 'editorCopyButtonText' })}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setCopyIndex(index)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={formatMessage({ id: 'editorDeleteButtonText' })}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setDeleteIndex(index)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {children(
                    jp,
                    validateJourneyPatternName,
                    updateJourneyPattern(index),
                    undefined,
                    undefined,
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}

        <AddButton
          onClick={() => setShowModal(true)}
          buttonTitle={formatMessage({ id: 'editorAddJourneyPatterns' })}
        />
      </Box>

      <ConfirmDialog
        isOpen={deleteIndex !== null}
        title={formatMessage({ id: 'journeyPatternDeleteDialogTitle' })}
        message={formatMessage({ id: 'journeyPatternDeleteDialogMessage' })}
        buttons={[
          <Button
            variant="outlined"
            key={2}
            onClick={() => setDeleteIndex(null)}
          >
            {formatMessage({ id: 'no' })}
          </Button>,
          <Button
            variant="contained"
            color="error"
            key={1}
            onClick={() => {
              if (deleteIndex !== null) {
                deleteJourneyPattern(deleteIndex)();
                setDeleteIndex(null);
              }
            }}
          >
            {formatMessage({ id: 'yes' })}
          </Button>,
        ]}
        onDismiss={() => setDeleteIndex(null)}
      />

      {copyIndex !== null && (
        <CopyDialog
          open={copyIndex !== null}
          journeyPattern={journeyPatterns[copyIndex]}
          onSave={(name) => {
            copyJourneyPattern(journeyPatterns, copyIndex, name);
            setCopyIndex(null);
          }}
          onDismiss={() => setCopyIndex(null)}
          validateJourneyPatternName={validateJourneyPatternName}
        />
      )}
    </>
  );
};

export default JourneyPatterns;
